import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, not, or } from 'ember-truth-helpers';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import SettingsBudgetForecastSegmentHierarchy from 'lumen/components/settings/budget-forecast/segment-hierarchy';
import type { BudgetForecastEntry, BudgetForecastSegment } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface SettingsBudgetForecastModalSegmentVisibilitySignature {
  Args: {
    entry: BudgetForecastEntry;
    onClose: (wereChangesMade: boolean) => void;
    segments: BudgetForecastSegment[];
    view: 'budget' | 'forecast';
  };
}

type SaveSegmentVisibilityQueryParams = {
  entry_id: string;
  subscription_id: string;
};

export default class SettingsBudgetForecastModalSegmentVisibility extends Component<SettingsBudgetForecastModalSegmentVisibilitySignature> {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  saveSegmentVisibilityTask = task(async (): Promise<void> => {
    const queryParams: SaveSegmentVisibilityQueryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id!,
      entry_id: this.args.entry.id!,
    };

    const hierarchyUrl = formatUrl(
      '/apigateway/v1/app/budgetforecast/hierarchy',
      queryParams,
      this.ajax.host,
    );

    const requestBody = {
      data: this.args.segments.map((segment) =>
        segment.serialize({ includeId: true }),
      ),
    };

    const segmentsThatBecameHidden = this.args.segments.filter(
      (segment) => segment.hasDirtyAttributes && segment.visible === false,
    );

    try {
      const response = await this.ajax.request<{ data: object[] }>(
        hierarchyUrl,
        {
          type: 'PUT',
          data: JSON.stringify(requestBody),
        },
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('budget-forecast/segment', response);
    } catch (error: unknown) {
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }

    this.usageAnalytics.track('Segment Visibility Changed', {
      eventTrigger: 'filters-applied',
    });

    // Unload data items for segments that became hidden to avoid displaying force distribution state.
    // Without this, the force distribution state sometimes remains visible for hidden segments.
    segmentsThatBecameHidden.forEach((segment) =>
      segment.dataItems.forEach((item) => item.unloadRecord()),
    );
    this.args.onClose(true);
  });

  get hasChanges(): boolean {
    return this.args.segments.some((segment) => segment.hasDirtyAttributes);
  }

  @action
  onClose(): void {
    this.args.segments.forEach((segment) => {
      segment.rollbackAttributes();
    });

    this.args.onClose(false);
  }

  <template>
    <PrismModal
      @onClose={{this.onClose}}
      @title={{if
        (eq @view "budget")
        (t "settings.budget.manage_segments.title")
        (t "settings.forecast.manage_segments.title")
      }}
      data-test-id="modal-segment-visibility"
    >
      <:default>
        <div class="modal--padded modal--medium flex flex-col gap-400">
          <p
            class="text-300--regular"
            data-test-id="modal-segment-visibility-description"
          >
            {{if
              (eq @view "budget")
              (t "settings.budget.manage_segments.description")
              (t "settings.forecast.manage_segments.description")
            }}
          </p>

          <SettingsBudgetForecastSegmentHierarchy @segments={{@segments}} />
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.onClose}}
            data-test-id="modal-segment-visibility-cancel"
          />
          <PrismButton
            @isDisabled={{or
              (not this.hasChanges)
              this.saveSegmentVisibilityTask.isRunning
            }}
            @isLoading={{this.saveSegmentVisibilityTask.isRunning}}
            @label={{t "general.save"}}
            @onClick={{this.saveSegmentVisibilityTask.perform}}
            data-test-id="modal-segment-visibility-save"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
